import React, { useEffect, useState } from 'react';
import { MessageSquare, Mail, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const REFUSAL = ['xray-refusal-universal', 'periodontal-refusal-universal', 'patient-treatment-refusal-universal'];

// Always returns an image: the backend falls back to a titled page when a document has no file.
export const thumbUrl = (kind, id, name) =>
  `${BACKEND}/api/dictation/thumbnail/${kind}/${encodeURIComponent(id || 'none')}?v=3&name=${encodeURIComponent(name || '')}`;

export const formTag = (consentId) => {
  if (REFUSAL.includes(consentId)) return { text: 'Informed refusal', cls: 'bg-rose-100 text-rose-800' };
  if (consentId === 'failure-to-complete-treatment-universal') return { text: 'Treatment not completed form', cls: 'bg-orange-100 text-orange-800' };
  if ((consentId || '').startsWith('compliance-')) return { text: 'Compliance form', cls: 'bg-amber-100 text-amber-800' };
  return { text: 'Informed consent', cls: 'bg-violet-100 text-violet-800' };
};
export const TAGS = {
  post: { text: 'Post-treatment instructions', cls: 'bg-emerald-100 text-emerald-800' },
  pre: { text: 'Pre-treatment instructions', cls: 'bg-sky-100 text-sky-800' },
  med: { text: 'Med Card', cls: 'bg-teal-100 text-teal-800' },
};

export const DocTag = ({ tag }) => (
  <span className={`inline-block text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 mr-1.5 align-middle ${tag.cls}`}>{tag.text}</span>
);

// Every document a Visit Card will send, with a key to toggle it on the card.
export const cardDocs = (card) => {
  const v = card.visit || {};
  const docs = [];
  (v.doneToday || []).forEach((p, i) => docs.push({ key: `doneToday:${i}`, tag: TAGS.post, name: p.name, kind: 'procedure', id: p.procedureId, on: p.selected !== false, action: 'opens a link to read it' }));
  (v.planned || []).forEach((p, i) => docs.push({ key: `planned:${i}`, tag: TAGS.pre, name: p.name, kind: 'procedure', id: p.procedureId, on: p.selected !== false, action: 'opens a link to read it' }));
  (card.forms || []).forEach((f, i) => docs.push({ key: `forms:${i}`, tag: formTag(f.consentId), name: f.name, kind: 'consent', id: f.consentId, on: !!f.selected, action: 'opens a link to sign it' }));
  (v.medicationsPrescribed || []).forEach((m, i) => m.medCardId && docs.push({ key: `meds:${i}`, tag: TAGS.med, name: m.name, kind: 'medcard', id: m.medCardId, on: m.selected !== false, action: 'opens a link to read it' }));
  return docs;
};

const applyChoices = (card, on, channels) => {
  const v = { ...(card.visit || {}) };
  const pick = (list, prefix) => (list || []).map((x, i) => ({ ...x, selected: !!on[`${prefix}:${i}`] }));
  v.doneToday = pick(v.doneToday, 'doneToday');
  v.planned = pick(v.planned, 'planned');
  v.medicationsPrescribed = (v.medicationsPrescribed || []).map((m, i) => (m.medCardId ? { ...m, selected: !!on[`meds:${i}`] } : m));
  return { ...card, visit: v, forms: pick(card.forms, 'forms'), channels };
};

const ChannelPicker = ({ contact, channels, setChannels, id }) => {
  const opts = [
    { key: ['sms'], label: 'Text', icon: MessageSquare, ok: !!contact.phone },
    { key: ['email'], label: 'Email', icon: Mail, ok: !!contact.email },
    { key: ['sms', 'email'], label: 'Both', icon: Send, ok: !!contact.phone && !!contact.email },
  ];
  return (
    <div className="flex gap-2" data-testid={`review-send-channels-${id}`}>
      {opts.map((o) => {
        const active = o.key.join() === channels.join();
        return (
          <button key={o.label} type="button" disabled={!o.ok} onClick={() => setChannels(o.key)}
            title={o.ok ? '' : `No ${o.key.length > 1 ? 'cell phone or email' : o.key[0] === 'sms' ? 'cell phone' : 'email'} on file for this patient`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${active ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'} disabled:opacity-60 disabled:line-through disabled:cursor-not-allowed`}
            data-testid={`review-send-channel-${o.label.toLowerCase()}-${id}`}>
            <o.icon className="h-4 w-4" />{o.label}
          </button>
        );
      })}
    </div>
  );
};

export const ReviewSendDialog = ({ open, onClose, card, name, contact, onConfirm }) => {
  const docs = cardDocs(card);
  const [on, setOn] = useState({});
  const [channels, setChannels] = useState([]);
  useEffect(() => {
    if (!open) return;
    setOn(Object.fromEntries(docs.map((d) => [d.key, d.on])));
    setChannels([contact.phone && 'sms', contact.email && 'email'].filter(Boolean));
  }, [open]); // eslint-disable-line
  const count = docs.filter((d) => on[d.key]).length;
  const where = [channels.includes('sms') && `a text to ${contact.phone}`, channels.includes('email') && `an email to ${contact.email}`].filter(Boolean).join(' and ');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={`review-send-dialog-${card.id}`}>
        <DialogHeader>
          <DialogTitle>Review before sending to <span className="capitalize">{name}</span></DialogTitle>
          <DialogDescription>Untick anything you don't want to send, and choose how it goes out.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-800">Send by</p>
          <ChannelPicker contact={contact} channels={channels} setChannels={setChannels} id={card.id} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {docs.map((d) => (
            <label key={d.key} className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-opacity ${on[d.key] ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 opacity-50'}`} data-testid={`review-send-doc-${d.key}`}>
              <Checkbox checked={!!on[d.key]} onCheckedChange={(c) => setOn((s) => ({ ...s, [d.key]: !!c }))} className="mt-1" data-testid={`review-send-doc-toggle-${d.key}`} />
              <img src={`${thumbUrl(d.kind, d.id, d.name)}&size=lg`} alt={d.name}
                className="h-40 w-28 object-contain object-top bg-slate-50 border rounded shrink-0" />
              <span className="text-sm">
                <DocTag tag={d.tag} />
                <span className="block font-semibold text-slate-900 mt-1">{d.name}</span>
                <span className="block text-xs text-slate-500 mt-1">The patient {d.action}.</span>
              </span>
            </label>
          ))}
        </div>
        <div className="flex items-center gap-3 border-t pt-3">
          <p className="text-sm text-slate-700" data-testid={`review-send-summary-${card.id}`}>
            {count && where ? `${count} document${count > 1 ? 's' : ''} will go out by ${where}.` : 'Pick at least one document and a way to send.'}
          </p>
          <Button variant="outline" className="ml-auto" onClick={onClose} data-testid={`review-send-back-${card.id}`}>Go back</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!count || !channels.length}
            onClick={() => onConfirm(applyChoices(card, on, channels))} data-testid={`review-send-confirm-${card.id}`}>
            <Send className="h-4 w-4 mr-1" /> Send now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewSendDialog;
